import { cookieStorage } from '../utils/cookieStorage';
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {fetchRoutePermissions, RoutePerm} from '../services/permissionService';
import {useAuth} from './AuthContext';
import {useLocation} from 'react-router-dom';
import {ROUTES} from '../routes/routePaths';
import {checkRouteAccess} from '../services/accessDecisionService';

type Change = {permission?: RoutePerm; deletedId?: number};
type PermissionSnapshot = {identity: string; rules: RoutePerm[]; paths: Set<string>; failed?: boolean};
interface PermissionsContextType {
    permissions: Record<string,string[]>;
    disabledPaths: string[];
    loading: boolean;
}
const PermissionsContext = createContext<PermissionsContextType>({permissions:{}, disabledPaths:[], loading:true});

export const PermissionsProvider: React.FC<{children:React.ReactNode}> = ({children}) => {
    const {isAuthenticated, user} = useAuth();
    const {pathname} = useLocation();
    const administrator = user?.role === 'Administrador';
    const identity = isAuthenticated ? `${user?.id}:${user?.role}` : null;
    const [snapshot, setSnapshot] = useState<PermissionSnapshot | null>(null);
    const [revision, setRevision] = useState(0);
    const generation = useRef(0);
    const identityRef = useRef(identity);
    identityRef.current = identity;
    const readyIdentity = useRef<string | null>(null);
    const adminRef = useRef(administrator);
    adminRef.current = administrator;

    useEffect(() => {
        let cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        const apply = (change?: Change) => {
            const activeIdentity = identityRef.current;
            if (!activeIdentity) return;
            if (!adminRef.current || readyIdentity.current !== activeIdentity ||
                (!change?.permission && change?.deletedId === undefined)) {
                setRevision(value => value + 1);
                return;
            }
            const {permission, deletedId} = change;
            generation.current += 1;
            setSnapshot(current => current?.identity === activeIdentity ? {
                ...current,
                rules: permission
                    ? [...current.rules.filter(rule => rule.id !== permission.id), permission]
                    : current.rules.filter(rule => rule.id !== deletedId),
            } : current);
        };
        const changed = (event: Event) => {
            const detail = (event as CustomEvent<Change>).detail;
            apply(detail);
            cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        };
        const focused = () => setRevision(value => value + 1);
        const timer = window.setInterval(() => {
            const current = cookieStorage.getItem('comarca_route_permissions_revision');
            if (current !== cookieRevision) {cookieRevision = current; setRevision(value => value + 1);}
        }, 500);
        window.addEventListener('route-permissions-changed', changed);
        window.addEventListener('focus', focused);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('route-permissions-changed', changed);
            window.removeEventListener('focus', focused);
        };
    }, []);

    useEffect(() => {
        let active = true;
        const version = generation.current += 1;
        if (!identity) {
            readyIdentity.current = null;
            setSnapshot(null);
            return;
        }
        if (readyIdentity.current !== identity) readyIdentity.current = null;
        setSnapshot(current => current?.identity === identity && !current.failed ? current : null);
        const paths = Array.from(new Set([...Object.values(ROUTES), pathname]));
        const request = administrator ? fetchRoutePermissions() : checkRouteAccess(paths).then(allowed =>
            paths.map((path, index) => ({id: index, path, activo: true,
                allowed_roles: allowed[index] && user?.role ? [user.role] : []})));
        request.then(list => {
            if (!active || generation.current !== version) return;
            readyIdentity.current = identity;
            setSnapshot({identity, rules: list, paths: new Set(paths)});
        }).catch(() => {
            if (!active || generation.current !== version) return;
            readyIdentity.current = null;
            setSnapshot({identity, rules: [], paths: new Set([pathname]), failed: true});
        });
        return () => {active = false;};
    }, [identity, user?.role, administrator, pathname, revision]);

    // A decision from a different session or path cannot settle the current route.
    const current = identity && snapshot?.identity === identity ? snapshot : null;
    const loading = Boolean(identity && !(current && ((administrator && !current.failed) || current.paths.has(pathname))));
    const rules = current?.rules;
    const permissions = useMemo(() => Object.fromEntries((rules ?? []).map(rule => [rule.path, rule.activo === false ? [] : rule.allowed_roles])), [rules]);
    const disabledPaths = useMemo(() => (rules ?? []).filter(rule => rule.activo === false).map(rule => rule.path), [rules]);
    return <PermissionsContext.Provider value={{permissions, disabledPaths, loading}}>{children}</PermissionsContext.Provider>;
};
export const usePermissions = () => useContext(PermissionsContext);
